/**
 * useTheme Hook
 *
 * Manages light/dark theme state with localStorage persistence.
 * Theme preference persists across browser sessions.
 */

import { useEffect, useState, useCallback } from 'react';

import { getStoredTheme, setStoredTheme } from '@/utils/storage.utils';

export type Theme = 'light' | 'dark';

/**
 * useTheme - Manage application theme (light/dark)
 *
 * @example
 * const { theme, setTheme, toggleTheme, isDark } = useTheme();
 *
 * // Toggle theme
 * <button onClick={toggleTheme}>
 *   {isDark ? 'Switch to Light' : 'Switch to Dark'}
 * </button>
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove both classes first
    root.classList.remove('light', 'dark');

    // Add the current theme class
    root.classList.add(theme);

    // Also update the color-scheme for native elements
    root.style.colorScheme = theme;
  }, [theme]);

  // Set theme and persist to localStorage
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return {
    /** Current theme */
    theme,
    /** Set theme explicitly */
    setTheme,
    /** Toggle between light and dark */
    toggleTheme,
    /** Whether current theme is dark */
    isDark: theme === 'dark',
    /** Whether current theme is light */
    isLight: theme === 'light',
  };
}
